/**
 * Aapka header v1.
 * Native Dawn cart sections stay authoritative. This script never replaces fetch.
 * Apps without Dawn cart events can dispatch `aapka:cart:refresh` after success.
 */
(() => {
  'use strict';

  if (window.__aapkaHeaderV1Installed) return;
  window.__aapkaHeaderV1Installed = true;

  class HeaderController extends HTMLElement {
    connectedCallback() {
      // Wait for child markup when the Theme Editor inserts a fresh section.
      queueMicrotask(() => {
        if (!this.isConnected || this.lifecycle) return;
        this.initialize();
      });
    }

    initialize() {
      this.lifecycle = new AbortController();
      this.section = this.closest('.section-header') || this;
      this.cartLink = this.querySelector('[data-aapka-cart-link]');
      this.stickyType = this.dataset.stickyType || 'none';
      this.headerIsAlwaysSticky = ['always', 'reduce-logo-size'].includes(this.stickyType);
      this.currentScrollTop = Math.max(0, window.scrollY);
      this.headerTop = this.section.getBoundingClientRect().top + window.scrollY;
      this.preventRevealUntil = 0;
      this.cartRevision = 0;
      this.refreshTimer = 0;
      this.frame = 0;
      this.measureFrame = 0;
      const { signal } = this.lifecycle;

      this.setHeaderHeight();
      if (this.headerIsAlwaysSticky) {
        this.section.classList.add('shopify-section-header-sticky');
      }

      window.addEventListener('scroll', () => this.queueScroll(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueMeasure(), { passive: true, signal });
      this.addEventListener('preventHeaderReveal', () => {
        this.preventRevealUntil = performance.now() + 150;
      }, { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueMeasure());
        this.resizeObserver.observe(this.section);
      }

      this.bindCartDrawer();
      this.connectCartEvents();
      this.queueScroll();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.requestController?.abort();
      this.resizeObserver?.disconnect();
      this.cartObserver?.disconnect();
      this.unsubscribeCart?.();
      clearTimeout(this.refreshTimer);
      clearTimeout(this.requestTimeout);
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.measureFrame);
      this.section?.classList.remove(
        'shopify-section-header-sticky', 'shopify-section-header-hidden', 'scrolled-past-header', 'animate'
      );
      this.lifecycle = null;
      this.unsubscribeCart = null;
    }

    queueMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        if (!this.isConnected) return;
        this.setHeaderHeight();
        this.queueScroll();
      });
    }

    setHeaderHeight() {
      if (!this.section) return;
      this.headerHeight = this.section.offsetHeight;
      if (window.scrollY < 1) {
        this.headerTop = this.section.getBoundingClientRect().top;
      }
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    }

    queueScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.isConnected) this.onScroll();
      });
    }

    onScroll() {
      const next = Math.max(0, window.scrollY);
      const previous = this.currentScrollTop;
      this.currentScrollTop = next;
      if (this.stickyType === 'none') return;

      const pastHeader = next > this.headerTop + this.headerHeight;
      this.section.classList.toggle('scrolled-past-header', pastHeader);
      if (this.headerIsAlwaysSticky) return;

      const search = this.querySelector('predictive-search');
      const isInteracting = this.contains(document.activeElement)
        || Boolean(this.querySelector('details[open]'))
        || Boolean(search?.isOpen)
        || Boolean(search?.hasAttribute('open'));

      if (next <= this.headerTop) {
        this.reset();
      } else if (isInteracting) {
        this.reveal();
      } else if (pastHeader && next > previous + 2 && !this.preventHide) {
        this.hide();
      } else if (pastHeader && next < previous - 2 && performance.now() > this.preventRevealUntil) {
        this.reveal();
      }
    }

    // Keep Dawn's public reveal/hide methods for cart-notification integration.
    reveal() {
      if (!this.section || this.stickyType === 'none') return;
      this.section.classList.add('shopify-section-header-sticky', 'animate');
      this.section.classList.remove('shopify-section-header-hidden');
    }

    hide() {
      if (this.headerIsAlwaysSticky || this.stickyType === 'none') return;
      this.section.classList.add('shopify-section-header-sticky', 'shopify-section-header-hidden');
      this.closeMenuDisclosure();
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.section.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
    }

    closeMenuDisclosure() {
      this.querySelectorAll('header-menu').forEach((menu) => {
        if (typeof menu.close === 'function') menu.close();
      });
    }

    bindCartDrawer() {
      if (!this.cartLink || this.dataset.cartType !== 'drawer') return;
      // On the first page load Dawn binds this native ID itself. Our fallback
      // is only for a new header inserted after the drawer was initialized.
      if (this.cartLink.getAttribute('role') === 'button' && this.cartLink.dataset.aapkaDrawerBound !== 'true') return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      this.cartLink.setAttribute('role', 'button');
      this.cartLink.setAttribute('aria-haspopup', 'dialog');
      this.cartLink.dataset.aapkaDrawerBound = 'true';
      const open = (event) => {
        if (event.type === 'keydown' && event.code !== 'Space') return;
        if (event.type === 'click' && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
        const currentDrawer = document.querySelector('cart-drawer');
        if (typeof currentDrawer?.open !== 'function') return;
        event.preventDefault();
        currentDrawer.open(this.cartLink);
      };
      const options = { signal: this.lifecycle.signal };
      this.cartLink.addEventListener('click', open, options);
      this.cartLink.addEventListener('keydown', open, options);
    }

    connectCartEvents() {
      const { signal } = this.lifecycle;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const markup = event?.cartData?.sections?.['cart-icon-bubble']
            || event?.data?.cartData?.sections?.['cart-icon-bubble'];
          if (typeof markup === 'string' && this.applyCartHTML(markup)) return;
          this.queueCartRefresh();
        });
      }

      ['aapka:cart:refresh', 'cart:updated', 'cart:refresh', 'cart:change'].forEach((name) => {
        document.addEventListener(name, () => this.queueCartRefresh(), { signal });
      });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.queueCartRefresh();
      }, { signal });
      window.addEventListener('focus', () => this.queueCartRefresh(), { signal });

      // Invalidate an older in-flight request if Dawn replaces the native markup.
      // Do not fetch in response to this observer: that would create a loop.
      if (this.cartLink) {
        this.lastCartMarkup = this.cartLink.innerHTML;
        this.cartObserver = new MutationObserver(() => {
          const markup = this.cartLink.innerHTML;
          if (markup === this.lastCartMarkup) return;
          this.lastCartMarkup = markup;
          this.cartRevision += 1;
          this.requestController?.abort();
        });
        this.cartObserver.observe(this.cartLink, { childList: true, subtree: true });
      }
    }

    queueCartRefresh() {
      clearTimeout(this.refreshTimer);
      if (!this.isConnected || !this.cartLink) return;
      this.refreshTimer = setTimeout(() => this.refreshCart(), 150);
    }

    applyCartHTML(html) {
      if (!this.cartLink || typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.shopify-section') || parsed.body;
      if (!source.querySelector('[data-aapka-header-cart-count]')) return false;

      const markup = source.innerHTML.trim();
      clearTimeout(this.refreshTimer);
      this.cartRevision += 1;
      this.requestController?.abort();
      if (this.cartLink.innerHTML.trim() !== markup) this.cartLink.innerHTML = markup;
      this.lastCartMarkup = this.cartLink.innerHTML;
      return true;
    }

    async refreshCart() {
      if (!this.isConnected || !this.cartLink) return;
      this.requestController?.abort();
      clearTimeout(this.requestTimeout);
      const controller = new AbortController();
      this.requestController = controller;
      const revision = ++this.cartRevision;

      try {
        // Locale-aware section HTML, not a hard-coded /cart.js or INR formatter.
        const url = new URL(this.dataset.rootUrl || '/', window.location.origin);
        if (url.origin !== window.location.origin) return;
        url.searchParams.set('sections', 'cart-icon-bubble');
        this.requestTimeout = setTimeout(() => controller.abort(), 8000);
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) return;
        const sections = await response.json();
        if (!this.isConnected || revision !== this.cartRevision) return;
        this.applyCartHTML(sections['cart-icon-bubble']);
      } catch (error) {
        // Keep the existing cart display and native link on network/API failure.
        if (error.name !== 'AbortError' && window.Shopify?.designMode) {
          console.debug('Aapka header: cart refresh failed.', error);
        }
      } finally {
        if (this.requestController === controller) {
          clearTimeout(this.requestTimeout);
          this.requestController = null;
        }
      }
    }
  }

  class AapkaStickyHeader extends HeaderController { }
  class AapkaStaticHeader extends HeaderController { }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', AapkaStickyHeader);
  }
  if (!customElements.get('aapka-header')) {
    customElements.define('aapka-header', AapkaStaticHeader);
  }
})();
